import {
    Backdrop,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    styled,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import moment from 'moment';
import React, { useCallback, useState } from 'react';
import { get } from '../../../lib/api';
import { useGet } from '../../../lib/api/hooks';
import { CaseClinicalPolicies } from '../../../lib/models/CaseClinicalPolicies';
import t from '../../../theme';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { TabContext, TabList } from '@mui/lab';
import ViewFileModal from './ViewFileModal';
import CloseIcon from '@mui/icons-material/Close';
import { useScreenSmallerThan } from '../../../lib/hooks/breakpoint-hooks';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';

interface CasesProps {
    caseId: string;
    crmId: string;
    clinicalReviewId: string;
}
const CasePoliciesAndClinicalsModal = ({ caseId, crmId, clinicalReviewId }: CasesProps) => {
    const [isDialogOpened, setDialogOpened] = useState(false);
    const [isDialogHidden, setIsDialogHidden] = useState(false);
    const [displayOrder, setDisplayOrder] = useState('asc');
    const [cases, setCases] = useState<CaseClinicalPolicies[]>([]);
    const [tabValue, setTabValue] = React.useState('CasePolices');
    const [inProgress, setInProgress] = useState(false);
    const [openedDocuments, setOpenedDocuments] = useState<(string | null)[]>([]);

    // Function to add a new document name to the list
    const addOpenedDocument = (documentName: string | null) => {
        setOpenedDocuments((prevDocuments) => [...prevDocuments, documentName]);
    };

    // Function to check whether a document is part of openedDocuments
    const isDocumentOpened = (documentName: string | null) => {
        return openedDocuments.includes(documentName);
    };

    const handleCloseDialog = () => {
        setDialogOpened(false);
    };
    const handleOpenDialog = () => {
        setDialogOpened(true);
        setIsDialogHidden(false);
    };
    const handleChange = (event: React.SyntheticEvent, value: string) => {
        setTabValue(value);
    };

    const isSmallerThanSmall = useScreenSmallerThan('sm');
    const isSmallerThanMedium = useScreenSmallerThan('md');
    const { data, loading } = useGet<CaseClinicalPolicies[]>(
        `casedetails/getcasepolicies?caseId=${caseId}&crmId=${crmId}&clinicalReviewId=${clinicalReviewId}`
    );

    const updateOpenedDocuments = (data: CaseClinicalPolicies[] | null) => {
        if (data) {
            const openedDocuments = data.filter((item) => item.isOpenedDocument);
            setOpenedDocuments(openedDocuments.map((item) => item.fileName));
        }
    };

    React.useEffect(() => {
        setCases(data ?? []);
        updateOpenedDocuments(data);
    }, [data, loading]);

    const getCasePolices = async () => {
        try {
            setInProgress(true);
            const result = await get(`casedetails/getcasepolicies?caseId=${caseId}&crmId=${crmId}`);

            if (result.data) {
                setCases(result.data);
                setInProgress(false);
                updateOpenedDocuments(result.data);
            }
        } catch (error) {
            console.log(error);
            setInProgress(false);
        }
    };

    const getCaseClinicals = async () => {
        try {
            setInProgress(true);
            const result = await get(
                `casedetails/getcaseclinicals?caseId=${caseId}&crmId=${crmId}`
            );

            if (result.data) {
                setCases(result.data);
                setInProgress(false);
                updateOpenedDocuments(result.data);
            }
        } catch (error) {
            console.log(error);
            setInProgress(false);
        }
    };

    const displayByUploadedDate = async () => {
        displayOrder === 'asc' ? setDisplayOrder('desc') : setDisplayOrder('asc');

        cases.reverse();
    };

    const onViewFileModalOpen = useCallback(() => {
        const html = document.querySelector('html') as HTMLHtmlElement;
        const body = document.body;
        const root = document.querySelector('#root') as HTMLDivElement;

        if (html.style.overflow === 'hidden') {
            html.style.removeProperty('overflow');
        }

        if (body.style.paddingRight !== '0px') {
            body.style.removeProperty('padding-right');
        }

        if (root.ariaHidden === 'true') {
            root.ariaHidden = null;
        }

        setIsDialogHidden(true);
    }, []);

    const onViewFileModalClose = useCallback(() => {
        const html = document.querySelector('html') as HTMLHtmlElement;
        const body = document.body;
        const root = document.querySelector('#root') as HTMLDivElement;

        if (html.style.overflow !== 'hidden') {
            html.style.overflow = 'hidden';
        }

        if (body.style.paddingRight === '') {
            body.style.paddingRight = '17px';
        }

        if (root.ariaHidden === null) {
            root.ariaHidden = 'true';
        }

        setIsDialogHidden(false);
    }, []);

    return (
        <>
            <Button variant="outlined" onClick={handleOpenDialog}>
                Case Policies & Clinicals
            </Button>

            <Dialog hidden={isDialogHidden} open={isDialogOpened} onClose={handleCloseDialog}>
                <DialogActions sx={{ justifyContent: 'flex-start' }}>
                    <TabContext value={tabValue}>
                        <TabList onChange={handleChange}>
                            <Tab
                                label="Case Policies"
                                onClick={getCasePolices}
                                value="CasePolices"
                            />
                            <Tab
                                label="Case Clinicals"
                                onClick={getCaseClinicals}
                                value="CaseClinicals"
                            />
                        </TabList>
                    </TabContext>
                    <IconButton
                        onClick={handleCloseDialog}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500]
                        }}>
                        <CloseIcon />
                    </IconButton>
                </DialogActions>

                <DialogContent
                    sx={{
                        minWidth: isSmallerThanSmall
                            ? '300px'
                            : isSmallerThanMedium
                            ? '600px'
                            : '640px',
                        justifyContent: 'flex-start',
                        padding: '10px'
                    }}>
                    {!inProgress ? (
                        <>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableHeaderCell
                                                sx={{
                                                    fontSize: isSmallerThanSmall ? '12px' : '13px',
                                                    padding: 0,
                                                    display: 'flex',
                                                    flexWrap: 'nowrap',
                                                    alignItems: 'center'
                                                }}>
                                                Uploaded Date
                                                <IconButton
                                                    onClick={displayByUploadedDate}
                                                    sx={{ paddingRight: 0, paddingLeft: 0 }}>
                                                    {displayOrder === 'asc' ? (
                                                        <ArrowDropDownIcon />
                                                    ) : (
                                                        <ArrowDropUpIcon />
                                                    )}
                                                </IconButton>
                                            </TableHeaderCell>
                                            <TableHeaderCell></TableHeaderCell>
                                            <TableHeaderCell sx={{ padding: 0 }}>
                                                File Name
                                            </TableHeaderCell>
                                            <TableHeaderCell></TableHeaderCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {cases.map((item, index) => (
                                            <TableRow key={index}>
                                                <TableDataCell
                                                    sx={{
                                                        padding: 0,
                                                        fontSize: isSmallerThanSmall
                                                            ? '12px'
                                                            : '16px'
                                                    }}>
                                                    {moment(item.uploadDate).format('MM/DD/YYYY')}
                                                </TableDataCell>
                                                <TableDataCell
                                                    sx={{
                                                        paddingRight: '10px',
                                                        paddingBottom: '1rem',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}>
                                                    {!isDocumentOpened(item.fileName) &&
                                                        (isSmallerThanSmall ? (
                                                            <PriorityHighIcon
                                                                sx={{
                                                                    padding: '2px'
                                                                }}
                                                                color="primary"
                                                            />
                                                        ) : (
                                                            <Chip label="New" color="primary" />
                                                        ))}
                                                </TableDataCell>
                                                <TableDataCell
                                                    sx={{
                                                        padding: 0,
                                                        fontWeight: 700,
                                                        fontSize: isSmallerThanSmall
                                                            ? '13px'
                                                            : '16px',
                                                        wordBreak: 'break-word',
                                                        width: '55%'
                                                    }}>
                                                    {item.fileName}
                                                </TableDataCell>
                                                <TableDataCell
                                                    sx={{
                                                        padding: 0
                                                    }}>
                                                    <ViewFileModal
                                                        fileName={item.fileName ?? ''}
                                                        fileType={item.fileType}
                                                        link={item.link ?? null}
                                                        currentTabValue={tabValue}
                                                        policyId={item.id}
                                                        crmId={crmId}
                                                        crmCaseId={caseId}
                                                        type={item.type}
                                                        onOpen={onViewFileModalOpen}
                                                        onClose={onViewFileModalClose}
                                                        updateDocument={addOpenedDocument}
                                                        clinicalReviewId={clinicalReviewId}
                                                    />
                                                </TableDataCell>
                                            </TableRow>
                                        ))}
                                        {cases.length === 0 && (
                                            <TableRow>
                                                <TableDataCell
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'flex-end'
                                                    }}>
                                                    No record to display
                                                </TableDataCell>
                                                <TableDataCell></TableDataCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    ) : (
                        <>
                            <Backdrop
                                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                                open={inProgress}>
                                <CircularProgress color="inherit" />
                            </Backdrop>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

const TableHeaderCell = styled(TableCell)({
    color: t.palette.cardSubtitle.main,
    fontSize: 13,
    borderBottom: 'none'
});

const TableDataCell = styled(TableCell)({
    border: 'none',
    paddingLeft: '10px',
    paddingBottom: 0
});

export default CasePoliciesAndClinicalsModal;
